import React, { useState, useEffect, useCallback } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { OfficeMap } from "./OfficeLocation";
import { CUSTOMER_SUPPORT_HOURS, VOICE_SUPPORT_HOURS } from "../utils/supportHours";

type Toast = { message: string; type: "success" | "error" } | null;

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [nameError, setNameError] = useState("");
  const [toast, setToast] = useState<Toast>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, fullName: value });
    // Clear error as soon as user fixes the name after a submit attempt
    if (nameError && value.trim().length >= 3) {
      setNameError("");
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData({ ...formData, phone: digits });

    if (digits.length > 0 && !/^[6-9]/.test(digits)) {
      setPhoneError("Mobile numbers must start with 6, 7, 8, or 9.");
    } else if (digits.length === 10 && !/^[6-9][0-9]{9}$/.test(digits)) {
      setPhoneError("Please enter a valid 10-digit mobile number.");
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name length before submission
    if (formData.fullName.trim().length < 3) {
      setNameError("Name must be at least 3 characters.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email,
        }),
      });
      const data = await response.json();
      if (data.success) {
        showToast("Message sent successfully! We'll get back to you soon.", "success");
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
        setNameError("");
      } else {
        showToast("Failed to send message. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      showToast("An error occurred. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-2 rounded-xl bg-[#fdf5f3] border border-gray-200 text-[#1a1a3e] font-light placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#CC1A1A]/20 focus:border-[#CC1A1A] transition-all duration-200";

  return (
    <div className="min-w-screen bg-white">

      {/* Toast Notification */}
      <div
        style={{
          position: "fixed",
          top: "24px",
          right: "24px",
          zIndex: 9999,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          opacity: toast ? 1 : 0,
          transform: toast ? "translateY(0) scale(1)" : "translateY(-16px) scale(0.96)",
          pointerEvents: toast ? "auto" : "none",
        }}
      >
        {toast && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              padding: "16px 20px",
              borderRadius: "14px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
              background: "#fff",
              borderLeft: `4px solid ${toast.type === "success" ? "#22c55e" : "#CC1A1A"}`,
              minWidth: "300px",
              maxWidth: "400px",
            }}
          >
            <span style={{ fontSize: "20px", lineHeight: "1", flexShrink: 0 }}>
              {toast.type === "success" ? "✅" : "❌"}
            </span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#1a1a3e", lineHeight: "1.4" }}>
                {toast.type === "success" ? "Message Sent!" : "Something went wrong"}
              </p>
              <p style={{ margin: "3px 0 0", fontSize: "12px", color: "#6b7280", fontWeight: 400, lineHeight: "1.5" }}>
                {toast.message}
              </p>
            </div>
            <button
              onClick={() => setToast(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#9ca3af",
                fontSize: "16px",
                lineHeight: "1",
                padding: "0",
                flexShrink: 0,
              }}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        )}
      </div>


      <div className="max-w-[1440px] mx-auto flex flex-col gap-16 px-4 sm:px-6 lg:px-12 py-20 md:py-32">
        <Navbar />

        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#CC1A1A]/10 text-[#CC1A1A] font-bold text-xs uppercase tracking-widest mb-8 sm:mt-0 mt-8 border border-[#CC1A1A]/10">
            <span className="w-2 h-2 rounded-full bg-[#CC1A1A] animate-pulse" />
            Support is just a message away
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold text-[#CC1A1A] tracking-tighter mb-4 leading-tight">
            Get in Touch
          </h1>

          <p className="text-gray-500 text-base font-light lg:text-lg">
            Have questions about your salary advance? Our team is here to provide quick and personal support.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* LEFT SIDE – Contact Info */}
          <div className="flex flex-col gap-10">

            {/* Info Card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-10 flex flex-col gap-10">
              <p className="text-[#1a1a3e] font-medium text-center sm:text-left text-xl sm:text-2xl leading-relaxed">
                Your financial peace of mind is our top priority.
              </p>

              <div className="space-y-8">
                {[
                  { emoji: "📞", label: "Support Line", value: import.meta.env.VITE_COMPANY_PHONE, href: `tel:${import.meta.env.VITE_COMPANY_PHONE}` },
                  { emoji: "📧", label: "Official Email", value: import.meta.env.VITE_COMPANY_EMAIL, href: `mailto:${import.meta.env.VITE_COMPANY_EMAIL}` },
                  { emoji: "📍", label: "Headquarters", value: import.meta.env.VITE_COMPANY_ADDRESS, href: undefined },
                  { emoji: "☎️", label: "Voice Support Hours", value: VOICE_SUPPORT_HOURS, href: undefined },
                  { emoji: "🕘", label: "Customer Support Hours", value: CUSTOMER_SUPPORT_HOURS, href: undefined },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5 group">
                    <div className="w-12 h-12 rounded-xl bg-[#fdf5f3] border border-gray-200 flex items-center justify-center text-xl transition-all shrink-0">
                      {item.emoji}
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-[#1a1a3e] font-bold hover:text-[#CC1A1A] transition-colors text-base leading-snug">{item.value}</a>
                      ) : (
                        <span className="text-[#1a1a3e] font-bold text-base leading-snug">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-[32px] overflow-hidden border border-gray-200 shadow-sm">
              <OfficeMap />
            </div>
          </div>

          {/* RIGHT SIDE – Contact Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-gray-200 p-10 flex flex-col gap-6"
          >
            <div>
              <h2 className="text-2xl font-medium text-[#1a1a3e] tracking-tight mb-2">
                Leave a Message
              </h2>
              <p className="text-gray-400 font-light">We'll get back to you as soon as possible.</p>
            </div>

            <div className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1a1a3e] ml-1">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleNameChange}
                  minLength={3}
                  title="Name must be at least 3 characters"
                  required
                  className={inputClass}
                />
                {nameError && (
                  <p className="text-xs text-red-500 ml-1 mt-1 flex items-center gap-1">
                    <span>⚠️</span> {nameError}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1a1a3e] ml-1">Email Address</label>
                <input type="email" name="email" placeholder="name@company.com" value={formData.email} onChange={handleChange} required className={inputClass} />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1a1a3e] ml-1">Phone Number</label>
                <div className="flex items-center rounded-xl bg-[#fdf5f3] border border-gray-200 focus-within:ring-2 focus-within:ring-[#CC1A1A]/20 focus-within:border-[#CC1A1A] transition-all duration-200">
                  <span className="px-3 py-2 text-[#1a1a3e] font-medium text-sm border-r border-gray-200 select-none whitespace-nowrap">🇮🇳 +91</span>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    inputMode="numeric"
                    pattern="[6-9][0-9]{9}"
                    maxLength={10}
                    title="Enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9"
                    required
                    className="flex-1 px-3 py-2 bg-transparent text-[#1a1a3e] font-light placeholder:text-gray-400 focus:outline-none"
                  />
                </div>
                {phoneError && (
                  <p className="text-xs text-red-500 ml-1 mt-1 flex items-center gap-1">
                    <span>⚠️</span> {phoneError}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1a1a3e] ml-1">Message</label>
                <textarea name="message" placeholder="How can we help you?" value={formData.message} onChange={handleChange} rows={4} className={inputClass} />
              </div>
            </div>

            <div className="flex items-start gap-3 px-1">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                required
                className="mt-1 h-5 w-5 accent-[#CC1A1A] border-gray-200 rounded cursor-pointer shrink-0"
              />
              <label htmlFor="consent" className="text-xs text-gray-500 font-medium leading-relaxed cursor-pointer">
                By checking this box, you hereby accept our{" "}
                <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] font-bold underline hover:text-red-800 transition-colors">
                  Terms &amp; Conditions
                </a>{" "}
                and{" "}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#CC1A1A] font-bold underline hover:text-red-800 transition-colors">
                  Privacy Policy
                </a>.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#CC1A1A] text-white rounded-xl text-base font-semibold tracking-tight uppercase hover:bg-red-800 transition-colors disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactUs;